import { getCSRFToken } from '@shared/cookie';
import Modal from '@shared/ui-modal';
import PasswordlessForm from '@shared/passwordless-form';
import { T_ } from '@shared/translation';
import template from 'lodash-es/template';
import { createLocationField } from '@shared/location-field';
import { createOneClickForm } from './one-click-form';
import { createPostForm } from './post-form';
import signupModalTemplateBody from './signup-modal-body.html';

/**
 * Returns a local post project form with a locationField, a login prompt
 * and submission logic.
 * @param {Object} options configuration options for createForm
 * @param {String} options.postProjectFormId id of form element
 * @param {String} options.locationFieldId location input field
 * @param {String} options.locationBtnId use current location button
 * @param {String} options.locationErrorAlertId container for
                    locationField errors
 * @callback options.onLocationSelected callback when user selects location
 * @param {String} options.modalID modal id for the login prompt
 * @param {Object} options.postBtn DOM button which triggers submission
 * @param {boolean} options.isLoggedIn if true, psotButton triggers submit,
 *                  otherwise, show login prompt
 * @function postProject triggers the sumbission of form
 * @function addCondition accepts a boolean function to add to the validation
                    before submission
*/
export default function createForm({
  postProjectFormId,
  locationFieldId,
  locationBtnId,
  locationErrorAlertId,
  onLocationSelected,
  trackingSection,
  modalID,
  postBtn,
  isLoggedIn,
}) {
  const hiddenForm = createOneClickForm(createPostForm(postProjectFormId));
  const locationField = createLocationField({
    inputFieldId: locationFieldId,
    locationBtnId,
    errorAlertId: locationErrorAlertId,
    parentForm: hiddenForm,
    autoCompleteCallback: onLocationSelected,
  });
  const passwordLessModal = new Modal(
    'local-ppp-funnel',
    {
      body: template(signupModalTemplateBody)({
        T_,
        modalID,
      }),
    },
    {
      backdropClose: false,
      onHide: () => {
        postBtn.removeAttribute('disabled');
      },
    },
  );

  const passwordLessForm = new PasswordlessForm({
    onSignupModeActivated: () => {
      const signupTncField = document.getElementById(
        `${modalID}-signup-tnc-copy`,
      );
      if (signupTncField) {
        signupTncField.classList.remove('is-hidden');
      }
    },
    onSignupModeDeactivated: () => {
      const signupTncField = document.getElementById(
        `${modalID}-signup-tnc-copy`,
      );
      if (signupTncField) {
        signupTncField.classList.add('is-hidden');
      }
    },
    submissionCallback: form => {
      hiddenForm.setEmail(form.email.value);
      hiddenForm.setNewUserName(form.newusername.value);
      hiddenForm.setNewPassword(form.newuserpasswd.value);

      function submit(data) {
        fireQTS('TriedFormSubmit');
        if (data.status === 'success') {
          hiddenForm.submit();
          fireQTS('PostALocalJob');
          passwordLessForm.submitEl.textContent = T_('Submitting...');
          passwordLessForm.submitEl.setAttribute('disabled', 'disabled');
        } else {
          passwordLessForm.showLoginError(data.msg);
        }
      }

      if (passwordLessForm.isLoginMode()) {
        fireQTS('TriedLoginThroughAjax');
        const formData = new FormData();
        formData.append('username', form.email.value);
        formData.append('passwd', form.newuserpasswd.value);
        formData.append('csrf_token', getCSRFToken());
        fetch('/ajax/login.php', {
          method: 'POST',
          credentials: 'same-origin',
          body: formData,
        })
          .then(response => response.json())
          .then(data => submit(data));
      } else {
        submit({ status: 'success' });
      }
    },
  });

  passwordLessForm.initForm(modalID);
  postBtn.addEventListener('click', event => {
    postBtn.setAttribute('disabled', 'disabled');
    event.preventDefault();
    passwordLessForm.reset();
    postProject();
  });

  function postProject() {
    const allFieldsValid = validate();
    if (allFieldsValid && isLoggedIn) {
      hiddenForm.submit();
      fireQTS('PostALocalJob');
    } else if (allFieldsValid && !isLoggedIn) {
      passwordLessModal.show();
    }
  }

  const conditions = [locationField.validate];
  function addCondition(condition) {
    conditions.push(condition);
  }

  function validate() {
    return conditions.reduce((prev, condition) => prev && condition(), true);
  }

  function fireQTS(label) {
    _t.push([
      'user_action',
      {
        section: `${trackingSection}LandingPage`,
        subsection: 'OneClick',
        label,
      },
    ]);
  }

  return {
    setJob: hiddenForm.setJob,
    setSubCategory: hiddenForm.setSubCategory,
    locationField,
    postProject,
    addCondition,
  };
}
